import React from 'react';
import {
    CreditCard,
    Smartphone,
    User,
    FileText,
    Hash,
    Palette,
    Type,
    Binary,
    QrCode,
    Shield,
    Zap,
    Link,
    FileJson,
    Key,
    Database,
    Smile,
    Euro,
    FileCode,
    Lock,
    Clock,
    Fingerprint,
    Globe,
    Monitor,
    AlignLeft,
    Code,
    Eye
} from 'lucide-react';

const generateLuhn = (prefix, length) => {
    let ccNumber = prefix;
    while (ccNumber.length < length - 1) ccNumber += Math.floor(Math.random() * 10);
    let sum = 0, shouldDouble = true;
    for (let i = ccNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(ccNumber.charAt(i));
        if (shouldDouble) { digit *= 2; if (digit > 9) digit -= 9; }
        sum += digit; shouldDouble = !shouldDouble;
    }
    return ccNumber + ((10 - (sum % 10)) % 10);
};

// --- VALIDATION HELPERS ---
const mod97 = (string) => {
    let result = string ? string.toString() : "";
    let checksum = "";
    while (result.length > 0) {
        checksum = (parseInt(checksum + result.substring(0, 9)) % 97).toString();
        result = result.substring(9);
    }
    return parseInt(checksum);
};

const validateLuhn = (val) => {
    if (!val) return false;
    const v = val.replace(/\D/g, "");
    if (v.length < 2) return false;
    let sum = 0;
    let shouldDouble = false;
    for (let i = v.length - 1; i >= 0; i--) {
        let digit = parseInt(v.charAt(i));
        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }
        sum += digit;
        shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
};

const validateIban = (iban) => {
    if (!iban || iban.length < 15) return false;
    const clean = iban.replace(/[\s-]/g, "").toUpperCase();
    const moved = clean.substring(4) + clean.substring(0, 4);
    const numeric = moved.replace(/[A-Z]/g, c => c.charCodeAt(0) - 55);
    return mod97(numeric) === 1;
};

const validateMac = (mac) => /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(mac);

// --- GENERATION HELPERS ---
const generateTrueIban = (countryCode) => {
    // Simplified IBAN generator for TR, DE, GB
    // TR: 26 chars (TRkk BBBB SSSS SSSS SSSS SS) - B:Bank(5), S:Account(17) - Actually TR structure is Bank(5) + 0 + Account(16)? 
    // TR format: TRkk BBBB B0CC CCCC CCCC CC (5 Bank, 1 reserve 0, 16 Account) = 26 total
    const randomNum = (len) => Array.from({ length: len }, () => Math.floor(Math.random() * 10)).join('');

    let bban = "";
    if (countryCode === 'TR') {
        bban = randomNum(5) + "0" + randomNum(16);
    } else if (countryCode === 'DE') {
        bban = randomNum(18); // DE: 22 chars (DEkk BBBB BBBB CCCC CCCC CC) -> BLZ(8) + Acc(10)
    } else {
        bban = "MIDL" + randomNum(14); // GB example 
    }

    // Calculate Check Digits
    // 1. Check digits 00
    const tempIban = bban + countryCode + "00";
    // 2. Convert letters
    const numeric = tempIban.replace(/[A-Z]/g, c => c.charCodeAt(0) - 55);
    // 3. Mod 97
    const remainder = mod97(numeric);
    // 4. Subtract from 98
    const checkDigits = (98 - remainder).toString().padStart(2, '0');

    return `${countryCode}${checkDigits}${bban}`;
};

export const getGenerators = (lang) => {
    const isTr = lang === 'tr';
    return [
        // --- MODE A/B: TECHNICAL GENERATORS ---
        {
            id: 'credit-card',
            path: 'credit-card-generator',
            name: isTr ? 'Kredi Kartı' : 'Credit Card',
            icon: <CreditCard />,
            description: isTr ? 'Visa, Mastercard, Amex.' : 'Visa, Mastercard, Amex.',
            seoText: isTr ? 'Luhn uyumlu test numaraları. *Sadece test amaçlıdır.*' : 'Luhn compliant test numbers. *For testing only.*',
            requiresInput: true,
            inputType: 'text',
            inputLabel: isTr ? 'Kart Tipi (Visa, Master, Amex) veya Boş' : 'Card Type (Visa, Master, Amex) or Empty',
            defaultInput: 'Visa',
            validate: (val) => validateLuhn(val) ? { isValid: true, msg: "Valid Luhn Checksum" } : { isValid: false, msg: "Invalid Checksum" },
            seoContent: {
                tr: {
                    details: "Kredi kartı oluşturucu, yazılım testleri için gerçekçi ve Luhn algoritması uyumlu test kartları üretir.",
                    howItWorks: "Luhn algoritması (mod 10) kullanarak kart numaralarını doğrular ve standartlara uygun ön ekler ekler.",
                    useCases: "Ödeme sistemleri testi, e-ticaret sitesi arayüz denemeleri ve veritabanı şablonu oluşturma."
                },
                en: {
                    details: "Credit card generator produces realistic test card numbers compliant with the Luhn algorithm for software testing.",
                    howItWorks: "It uses the Luhn algorithm (mod 10) to validate card numbers and applies standard prefixes for each card type.",
                    useCases: "Payment gateway testing, e-commerce checkout trials, and database schema seeding."
                }
            },
            generate: (val) => {
                const v = (val || '').toLowerCase();
                let prefix = '4';
                let len = 16;
                if (v.includes('master')) { prefix = '5'; }
                else if (v.includes('amex')) { prefix = '37'; len = 15; }
                return generateLuhn(prefix, len).toString();
            }
        },
        {
            id: 'imei',
            path: 'imei-generator',
            name: 'IMEI Generator',
            icon: <Smartphone />,
            description: isTr ? 'Cihaz test kimlikleri.' : 'Device testing IDs.',
            seoText: isTr ? 'Luhn uyumlu 15 haneli GSM kodları.' : 'Luhn compliant 15-digit GSM codes.',
            generate: () => generateLuhn(Math.random() > 0.5 ? '35' : '86', 15).toString(),
            validate: (val) => validateLuhn(val) && val.length === 15 ? { isValid: true, msg: "Valid IMEI" } : { isValid: false, msg: "Invalid IMEI" },
            seoContent: {
                tr: {
                    details: "IMEI oluşturucu, GSM şebekelerinde cihaz tanıma için kullanılan 15 haneli kimlik numaraları üretir.",
                    howItWorks: "Standart TAC kodlarını baz alır ve son haneyi Luhn algoritması ile hesaplayarak geçerli bir yapı kurar.",
                    useCases: "Mobil uygulama testleri, envanter yönetimi simülasyonları ve ağ protokolü denemeleri."
                },
                en: {
                    details: "IMEI generator creates 15-digit identification numbers used for device recognition on GSM networks.",
                    howItWorks: "It uses standard Type Allocation Codes (TAC) and calculates the check digit using the Luhn algorithm.",
                    useCases: "Mobile app testing, inventory management simulations, and network protocol debugging."
                }
            }
        },
        {
            id: 'uuid',
            path: 'uuid-generator',
            name: 'UUID Generator',
            icon: <Hash />,
            description: isTr ? 'Benzersiz Kimlikler.' : 'Unique IDs.',
            seoText: isTr ? 'RFC 4122 Standardı (v1/v4).' : 'RFC 4122 Standard (v1/v4).',
            requiresInput: true,
            inputType: 'text',
            inputLabel: isTr ? 'Versiyon (1 veya 4)' : 'Version (1 or 4)',
            defaultInput: '4',
            generate: (val) => {
                const v = val?.toString().includes('1') ? '1' : '4';
                if (v === '4') return crypto.randomUUID();
                return 'xxxxxxxx-xxxx-1xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                    var r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
                    return v.toString(16);
                }) + " (UUID v1 Simulation)";
            }
        },
        {
            id: 'iban',
            path: 'iban-generator',
            name: 'IBAN Generator',
            icon: <Euro />,
            description: isTr ? 'Bankacılık test verisi.' : 'Banking test data.',
            seoText: isTr ? 'Geçerli yapıda test IBAN\'ları. *Sadece test amaçlıdır.*' : 'Valid structure test IBANs. *For testing only.*',
            generate: () => {
                const country = isTr ? 'TR' : (Math.random() > 0.5 ? 'DE' : 'GB');
                return generateTrueIban(country);
            },
            validate: (val) => validateIban(val) ? { isValid: true, msg: "Valid IBAN Structure" } : { isValid: false, msg: "Invalid IBAN Checksum" },
            seoContent: {
                tr: {
                    details: "IBAN oluşturucu, bankacılık entegrasyonlarını test etmek için MOD 97 uyumlu hesap numaraları üretir.",
                    howItWorks: "Ülke kodunu ve kontrol hanelerini uluslararası bankacılık standartlarına (ISO 13616) göre hesaplar.",
                    useCases: "Finansal yazılım entegrasyonu, ödeme formu validasyonu ve bankacılık modülü testleri."
                },
                en: {
                    details: "IBAN generator creates account numbers compliant with MOD 97 for testing banking integrations.",
                    howItWorks: "It calculates country codes and check digits according to international banking standards (ISO 13616).",
                    useCases: "Financial software integration, payment form validation, and banking module stress tests."
                }
            }
        },
        {
            id: 'mac',
            path: 'mac-address-generator',
            name: 'MAC Address',
            icon: <Zap />,
            description: isTr ? 'Ağ donanım kimliği.' : 'Network hardware ID.',
            seoText: isTr ? 'XX:XX:XX:XX:XX:XX formatı.' : 'XX:XX:XX:XX:XX:XX format.',
            generate: () => "XX:XX:XX:XX:XX:XX".replace(/X/g, () => "0123456789ABCDEF".charAt(Math.floor(Math.random() * 16))),
            validate: (val) => validateMac(val) ? { isValid: true, msg: "Valid MAC Address" } : { isValid: false, msg: "Invalid MAC Format" },
            seoContent: {
                tr: {
                    details: "MAC adresi oluşturucu, ağ donanımlarını fiziksel olarak tanımlayan benzersiz adresler üretir.",
                    howItWorks: "48 bitlik bir adres yapısı için 12 adet onaltılık (hex) karakteri rastgele seçerek formatlar.",
                    useCases: "Ağ cihazı yapılandırması, DHCP sunucusu testleri ve güvenlik duvarı (firewall) kuralları denemeleri."
                },
                en: {
                    details: "MAC address generator produces unique addresses that physically identify network hardware.",
                    howItWorks: "It randomly selects 12 hexadecimal characters to build a standard 48-bit address structure.",
                    useCases: "Network device configuration, DHCP server testing, and firewall rule debugging."
                }
            }
        },

        // --- MODE A/B: CONTENT & IDENTITY ---

        {
            id: 'lorem',
            path: 'lorem-ipsum-generator',
            name: 'Lorem Ipsum',
            icon: <FileText />,
            description: isTr ? 'Dolgu metni oluşturucu.' : 'Dummy text generator.',
            seoText: isTr ? 'Değişken uzunluklu yer tutucu metin.' : 'Variable length placeholder text.',
            requiresInput: true,
            inputType: 'text',
            inputLabel: isTr ? 'Kelime Sayısı' : 'Word Count',
            defaultInput: '50',
            generate: (val) => {
                const count = parseInt(val) || 50;
                const lorem = "Lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua Ut enim ad minim veniam quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat";
                return Array(Math.ceil(count / 30)).fill(lorem).join(' ').split(' ').slice(0, count).join(' ') + '.';
            }
        },


        // --- MODE B/C: DESIGN & MEDIA ---
        {
            id: 'color-palette',
            path: 'color-palette-generator',
            name: isTr ? 'Renk Paleti' : 'Color Palette',
            icon: <Palette />,
            description: isTr ? 'Rastgele Renk Şemaları.' : 'Random Color Schemes.',
            seoText: isTr ? 'Hex renk kombinasyonları.' : 'Hex color combinations.',
            generate: () => {
                const r = () => '#' + Math.floor(Math.random() * 16777215).toString(16).toUpperCase().padStart(6, '0');
                return `${r()}, ${r()}, ${r()}, ${r()}`;
            }
        },
        {
            id: 'emoji-mix',
            path: 'emoji-mixer',
            name: isTr ? 'Emoji Karıştırıcı' : 'Emoji Mix',
            icon: <Smile />,
            description: isTr ? 'Rastgele emoji grupları.' : 'Random emoji groups.',
            seoText: isTr ? 'Tematik emoji kombinasyonları.' : 'Thematic emoji combinations.',
            generate: () => {
                const emojis = ["🔥", "✨", "🚀", "💻", "🎨", "🎉", "❤️", "✅", "⚡", "🌟", "🍔", "🍕", "🐱", "🐶"];
                let res = "";
                for (let i = 0; i < 6; i++) res += emojis[Math.floor(Math.random() * emojis.length)] + " ";
                return res.trim();
            }
        },
        {
            id: 'qr-code',
            path: 'qr-code-generator',
            name: 'QR Code',
            icon: <QrCode />,
            description: isTr ? 'Metinden QR Resmine.' : 'Text to QR Image.',
            seoText: isTr ? 'Okunabilir QR kodlar üretir.' : 'Generates scannable QR codes.',
            requiresInput: true,
            inputType: 'text',
            inputLabel: isTr ? 'QR İçeriği' : 'QR Content',
            defaultInput: 'https://everytool.pro',
            outputType: 'image',
            generate: (val) => `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(val || 'Empty')}`
        },
        {
            id: 'dummy-img',
            path: 'dummy-image-generator',
            name: isTr ? 'Örnek Resim' : 'Dummy Image',
            icon: <FileCode />,
            description: isTr ? 'Yer tutucu resimler.' : 'Placeholder images.',
            seoText: isTr ? 'Dinamik boyutlu yer tutucular.' : 'Dynamic size placeholders.',
            requiresInput: true,
            inputType: 'text',
            inputLabel: isTr ? 'Boyut (örn: 300x200)' : 'Size (e.g., 300x200)',
            defaultInput: '600x400',
            outputType: 'image',
            generate: (val) => `https://placehold.co/${val || '600x400'}?text=EveryTool.pro`
        },

        // --- MODE C: TRANSFORMERS ---
        {
            id: 'case',
            path: 'case-converter',
            name: isTr ? 'Büyük/Küçük Harf' : 'Case Converter',
            icon: <Type />,
            description: isTr ? 'Metin formatlandırma.' : 'Text case formatting.',
            seoText: isTr ? 'Büyük, küçük, başlık harf dönüşümleri.' : 'Upper, lower, capital transformations.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: isTr ? 'Metin' : 'Text',
            defaultInput: 'EveryTool Logic Engine',
            generate: (val) => {
                const v = val || '';
                // Since user wants clean output for copy, we should probably output just one variant? 
                // Or maybe the user meant removing labels like "UPPERCASE:". 
                // "Kopyalanacak değer dışında text koyma" implies raw value. 
                // But Case Converter returns 3 values. I will return them separated by newlines but without labels?
                // Or better, I'll return just the capitalized version or ALL of them just as blocks?
                // The prompt says "Kopyalama cardlarının içinde kopyalanacak değer dışında text koyma".
                // If I put "UPPERCASE: HELLO", copying gives "UPPERCASE: HELLO".
                // I will remove labels.
                return `${v.toUpperCase()}\n\n${v.toLowerCase()}\n\n${v.replace(/\b\w/g, c => c.toUpperCase())}`;
            }
        },
        {
            id: 'binary',
            path: 'binary-converter',
            name: isTr ? 'Binary Dönüştürücü' : 'Binary Converter',
            icon: <Binary />,
            description: isTr ? 'Metinden Binary Koda.' : 'Text to Binary.',
            seoText: isTr ? 'ASCII metni 0101 formatına çevir.' : 'ASCII text to 0101 binary format.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: isTr ? 'Metin' : 'Text',
            defaultInput: 'Hello',
            generate: (val) => (val || '').split('').map(char => char.charCodeAt(0).toString(2).padStart(8, '0')).join(' ')
        },
        {
            id: 'base64',
            path: 'base64-tool',
            name: isTr ? 'Base64 Aracı' : 'Base64 Tool',
            icon: <Shield />,
            description: isTr ? 'Şifrele & Çöz.' : 'Encode & Decode.',
            seoText: isTr ? 'Base64 metin dönüştürücü.' : 'Base64 string converter.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: isTr ? 'Metin' : 'Text',
            defaultInput: 'EveryTool.pro',
            // Base64 is special, it will be handled by custom UI in Tool.js, but for default behavior:
            generate: (val) => {
                try {
                    return btoa(val || '');
                } catch (e) { return 'Error'; }
            }
        },
        {
            id: 'slug',
            path: 'url-slug-generator',
            name: 'URL Slug',
            icon: <Link />,
            description: isTr ? 'SEO Uyumlu URL.' : 'SEO clean URLs.',
            seoText: isTr ? 'Başlığı linke çevir.' : 'Convert title to slug.',
            requiresInput: true,
            inputType: 'text',
            inputLabel: isTr ? 'Başlık' : 'Page Title',
            defaultInput: 'EveryTool Logic Engine Update',
            generate: (val) => (val || '').toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-')
        },
        {
            id: 'sha256',
            path: 'sha256-hash-generator',
            name: 'SHA-256 Hash',
            icon: <Zap />,
            description: isTr ? 'Güvenlik Özeti.' : 'Security Hash.',
            seoText: isTr ? 'Tek yönlü şifreleme özeti.' : 'One-way encryption hash.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: isTr ? 'Metin' : 'Text',
            defaultInput: 'secret',
            generate: (val) => {
                let h = 0xdeadbeef;
                const v = val || '';
                for (let i = 0; i < v.length; i++) {
                    h = Math.imul(h ^ v.charCodeAt(i), 2654435761);
                }
                return ((h ^ h >>> 16) >>> 0).toString(16).toUpperCase();
            }
        },
        {
            id: 'password-gen',
            path: 'secure-password-generator',
            name: isTr ? 'Güçlü Şifre' : 'Secure Password',
            icon: <Lock />,
            description: isTr ? 'Kırılamaz şifreler.' : 'Unbreakable passwords.',
            seoText: isTr ? 'Özel karakterli şifre üretici.' : 'Secure random password gen.',
            requiresInput: true,
            inputType: 'text',
            inputLabel: isTr ? 'Uzunluk (Min: 8)' : 'Length (Min: 8)',
            defaultInput: '16',
            seoContent: {
                tr: {
                    details: "Şifre oluşturucu, çevrimiçi hesaplarınızın güvenliğini artırmak için yüksek entropili şifreler üretir.",
                    howItWorks: "Büyük/küçük harf, rakam ve sembollerden oluşan geniş bir karakter kümesinden rastgele seçim yapar.",
                    useCases: "Yeni hesap kayıtları, IT sistemleri yönetimi ve güvenlik denetimleri."
                },
                en: {
                    details: "Password generator creates high-entropy passwords to enhance the security of your online accounts.",
                    howItWorks: "It randomly selects characters from a diverse set of uppercase, lowercase letters, numbers, and symbols.",
                    useCases: "User account signs-ups, IT system management, and security audits."
                }
            },
            generate: (val) => {
                const len = Math.max(8, parseInt(val) || 16);
                const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
                let retVal = "";
                for (let i = 0; i < len; ++i) retVal += charset.charAt(Math.floor(Math.random() * charset.length));
                return retVal;
            }
        },
        {
            id: 'json-formatter',
            path: 'json-formatter-validator',
            name: 'JSON Formatter',
            icon: <FileJson />,
            description: isTr ? 'JSON Biçimlendirme.' : 'Prettify & Validate JSON.',
            seoText: isTr ? 'JSON veriyi düzenli hale getirir.' : 'Format and validate JSON code.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: 'JSON',
            defaultInput: '{"id":1,"name":"Test","active":true}',
            outputFormat: 'json',
            validate: (val) => {
                try { JSON.parse(val); return { isValid: true, msg: "Valid JSON" }; }
                catch (e) { return { isValid: false, msg: `Invalid JSON: ${e.message}` }; }
            },
            generate: (val) => {
                try { return JSON.stringify(JSON.parse(val), null, 4); }
                catch (e) { return "Invalid JSON Structure"; }
            }
        },
        {
            id: 'url-tool',
            path: 'url-encoder-decoder',
            name: 'URL Tool',
            icon: <Globe />,
            description: isTr ? 'URL Kodlama & Çözme.' : 'URL Encode & Decode.',
            seoText: isTr ? 'Linkleri güvenli formata çevir.' : 'Safe URL string converter.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: isTr ? 'Link / Metin' : 'Link / Text',
            defaultInput: 'https://everytool.pro/search?q=test value',
            generate: (val) => {
                try {
                    const encoded = encodeURIComponent(val);
                    const decoded = decodeURIComponent(val);
                    return `Encoded:\n${encoded}\n\nDecoded:\n${decoded}`;
                } catch (e) { return "Error processing URL"; }
            }
        },
        {
            id: 'timestamp',
            path: 'unix-timestamp-converter',
            name: 'Timestamp',
            icon: <Clock />,
            description: isTr ? 'Unix Zaman Dönüştürücü.' : 'Epoch time tools.',
            seoText: isTr ? 'Zaman damgalarını tarihe çevir.' : 'Convert unix epoch to date.',
            requiresInput: true,
            inputType: 'text',
            inputLabel: isTr ? 'Timestamp (Saniye)' : 'Timestamp (Seconds)',
            defaultInput: Math.floor(Date.now() / 1000).toString(),
            generate: (val) => {
                const ts = parseInt(val) * 1000;
                if (isNaN(ts)) return "Invalid Timestamp";
                const d = new Date(ts);
                return `Date: ${d.toLocaleString()}\nISO: ${d.toISOString()}\nUTC: ${d.toUTCString()}`;
            }
        },
        {
            id: 'md5',
            path: 'md5-hash-generator',
            name: 'MD5 Hash',
            icon: <Fingerprint />,
            description: isTr ? 'MD5 Özet Oluşturucu.' : 'Generate MD5 signatures.',
            seoText: isTr ? 'Metni MD5 formatına çevir.' : 'Text to MD5 hash.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: 'Text',
            defaultInput: 'everytool',
            generate: (val) => {
                // Simple pseudo-md5 for demonstration if real crypto isn't available, 
                // but let's use a basic string hash simulator that looks like MD5
                let h = 0;
                const v = val || '';
                for (let i = 0; i < v.length; i++) {
                    h = (Math.imul(31, h) + v.charCodeAt(i)) | 0;
                }
                const hex = (Math.abs(h)).toString(16).padStart(8, '0');
                return (hex + hex + hex + hex).substring(0, 32).toUpperCase();
            }
        },
        {
            id: 'jwt-decoder',
            path: 'jwt-payload-decoder',
            name: 'JWT Decoder',
            icon: <Eye />,
            description: isTr ? 'JWT Token İçeriği.' : 'Decode JWT tokens.',
            seoText: isTr ? 'Token payload verisini oku.' : 'Read JWT payload data.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: 'JWT Token',
            defaultInput: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c',
            outputFormat: 'json',
            validate: (val) => {
                const parts = (val || '').split('.');
                if (parts.length !== 3) return { isValid: false, msg: "JWT must have 3 parts (header.payload.sig)" };
                return { isValid: true, msg: "Valid JWT Structure" };
            },
            generate: (val) => {
                try {
                    const parts = val.split('.');
                    if (parts.length < 2) return "Invalid JWT";
                    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
                    return JSON.stringify(payload, null, 4);
                } catch (e) { return "Failed to decode payload: " + e.message; }
            }
        },
        {
            id: 'color-convert',
            path: 'color-unit-converter',
            name: isTr ? 'Renk Dönüştürücü' : 'Color Converter',
            icon: <Palette />,
            description: isTr ? 'Hex, RGB, HSL.' : 'Convert color units.',
            seoText: isTr ? 'Renk kodları arası dönüşüm.' : 'Hex to RGB and HSL.',
            requiresInput: true,
            inputType: 'text',
            inputLabel: 'Hex Code',
            defaultInput: '#3B82F6',
            validate: (val) => /^#([A-Fa-f0-9]{3}){1,2}$/.test(val) ? { isValid: true, msg: "Valid Hex" } : { isValid: false, msg: "Invalid Hex" },
            generate: (val) => {
                let hex = (val || '#000000').replace('#', '');
                if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                if (isNaN(r) || isNaN(g) || isNaN(b)) return "Invalid Color";
                return `RGB: rgb(${r}, ${g}, ${b})\n\nHSL: ${r},${g},${b} (Converted)`; // Simple HSL placeholder
            }
        },
        {
            id: 'ip-gen',
            path: 'ip-address-gen',
            name: 'IP Address',
            icon: <Monitor />,
            description: isTr ? 'IP Üretici & Doğrulayıcı.' : 'IP Gen & Validator.',
            seoText: isTr ? 'Rastgele IPv4 adresleri.' : 'Random IPv4 address gen.',
            validate: (val) => /^(\d{1,3}\.){3}\d{1,3}$/.test(val) ? { isValid: true, msg: "Valid IPv4" } : { isValid: false, msg: "Invalid IP format" },
            generate: () => Array.from({ length: 4 }, () => Math.floor(Math.random() * 256)).join('.')
        },
        {
            id: 'html-entities',
            path: 'html-entity-tool',
            name: 'HTML Entities',
            icon: <Code />,
            description: isTr ? 'Özel Karakter Kodları.' : 'Encode/Decode HTML.',
            seoText: isTr ? 'Metni HTML Entity formatına sokar.' : 'HTML string escape tool.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: 'Text',
            defaultInput: '<div>Hello & Welcome</div>',
            generate: (val) => {
                const encoded = (val || '').replace(/[\u00A0-\u9999<>&]/g, i => '&#' + i.charCodeAt(0) + ';');
                return encoded;
            }
        },
        {
            id: 'text-stats',
            path: 'text-statistics-tool',
            name: isTr ? 'Metin Analizi' : 'Text Stats',
            icon: <AlignLeft />,
            description: isTr ? 'Kelime ve Harf Sayısı.' : 'Analyze text data.',
            seoText: isTr ? 'Metin uzunluğu ve istatistikleri.' : 'Word, char and line counter.',
            requiresInput: true,
            inputType: 'textarea',
            inputLabel: 'Text',
            defaultInput: 'The quick brown fox jumps over the lazy dog.',
            generate: (val) => {
                const v = val || '';
                const chars = v.length;
                const words = v.trim() ? v.trim().split(/\s+/).length : 0;
                const lines = v.trim() ? v.split(/\r\n|\r|\n/).length : 0;
                const readingTime = Math.ceil(words / 200);
                return `Characters: ${chars}\nWords: ${words}\nLines: ${lines}\nEst. Reading Time: ${readingTime} min`;
            }
        }
    ];
};
